/* eslint-disable @typescript-eslint/no-explicit-any */
import { getCachedResponse, setCachedResponse } from "@/lib/indexedDbCache";
import axios from "axios";
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { getUserInfo } from "./userService";

const baseApiUrl = `${window.location.protocol}//api.${window.location.hostname}/api`;
const api: AxiosInstance = axios.create({
  baseURL: window.location.hostname == "localhost" ? "http://localhost:3301/api" : baseApiUrl,
  timeout: 60000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Define include/exclude rules
const cacheIncludeList: string[] = []; // Add URLs to include (e.g., ["/users", "/posts"])
const cacheExcludeList: string[] = []; // Add URLs to exclude

// Hash generator for params (simple key generator)
const generateCacheKey = (url: string, params?: object) => {
  return `${url}?${params ? JSON.stringify(params) : ""}`;
};

const shouldCache = (url: string) => {
  if (cacheIncludeList.length) return cacheIncludeList.includes(url);
  if (cacheExcludeList.length) return !cacheExcludeList.includes(url);
  return false; // cache everything by default
};

// ✅ Request interceptor (attach token)
api.interceptors.request.use(
  (config: any) => {
    const userInfo = getUserInfo();
    if (userInfo) {
      const token = userInfo.token
      if(token) config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: any) => Promise.reject(error)
);

// ✅ Response interceptor (handle errors globally)
api.interceptors.response.use(
  (response: any) => response,
  (error: any) => {
    if (error.response?.status === 401) {
      // Optional: Redirect on unauthorized
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

// ✅ Generic request wrapper
const apiService = {
  get: async <T>(url: string, params?: object, config?: AxiosRequestConfig): Promise<T> => {
    const cacheKey = generateCacheKey(url, params);

    if (shouldCache(url)) {
      const cached = await getCachedResponse(cacheKey);
      if (cached) return cached as T;
    }

    const response: AxiosResponse<T> = await api.get(url, { params, ...config });
    const data = response.data;

    if (shouldCache(url)) {
      await setCachedResponse(cacheKey, data);
    }

    return data;
  },

  post: async <T, D = unknown>(url: string, data: D, config?: AxiosRequestConfig): Promise<T> => {
    const response: AxiosResponse<T> = await api.post(url, data, config);
    return response.data;
  },

  put: async <T, D = unknown>(url: string, data: D, config?: AxiosRequestConfig): Promise<T> => {
    const response: AxiosResponse<T> = await api.put(url, data, config);
    return response.data;
  },

  patch: async <T, D = unknown>(url: string, data: D, config?: AxiosRequestConfig): Promise<T> => {
    const response: AxiosResponse<T> = await api.patch(url, data, config);
    return response.data;
  },

  delete: async <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
    const response: AxiosResponse<T> = await api.delete(url, config);
    return response.data;
  },
};

export default apiService;
